import type {
    AIFlowSchema,
    SchemaSection,
    Action,
    Condition,
    Decision,
    Section,
    Question,
    ActionNodeData,
    SectionNodeData,
    QuestionNodeData,
    DecisionNodeData,
    VisibilityConditions,
    DecisionCompletedCondition,
    FlowEdge,
    FlowNode,
    ContextItem,
    ContextVariable,
} from '@app/services/aiFlow.types';

function rewriteContextVariables(text: string, context: ContextVariable[]) {
    // Handle {{ context.var | default('value') }}
    text = text.replace(
        /{{\s*context\.(\w+)\s*\|\s*default\('([^']*)'\)\s*}}/g,
        (_match, varName, defaultValue) => {
            const ctx = context.find((c) => c.name === varName);
            if (ctx && !ctx.defaultValue) ctx.defaultValue = defaultValue;
            return `{{ ${varName} }}`;
        }
    );
    // Handle {{ context.var }}
    text = text.replace(/{{\s*context\.(\w+)\s*}}/g, (_match, varName) => `{{ ${varName} }}`);
    return text;
}

function isDecision(section: SchemaSection): section is Decision {
    const { visibility_conditions } = section;
    if (
        visibility_conditions &&
        typeof visibility_conditions === 'object' &&
        'ref' in visibility_conditions
    ) {
        return visibility_conditions.ref.type === 'metadata';
    } else {
        return false;
    }
}

function mapDecisionNodeIds(mappedIds: Record<string, string>, keys: string[], value: string) {
    const copy = { ...mappedIds };
    keys.forEach((key) => {
        copy[key] = value;
    });
    return copy;
}

function createConditionSourceHandle(condition: Condition | DecisionCompletedCondition) {
    let sourceHandle = `${condition.ref.type}`;
    if (condition.comparison === 'equals') {
        sourceHandle += `-value:${condition.value}-source`;
    } else {
        sourceHandle += `-complete-source`;
    }
    return sourceHandle;
}

function createConditionEdges(
    visibilityConditions: Exclude<VisibilityConditions, { ref: { type: 'metadata' } }>,
    currentId: string,
    mappedDecisionIds: Record<string, string>
) {
    const edges = [];
    let conditionGroups = 1;
    if ('logical' in visibilityConditions) {
        if (visibilityConditions.logical === 'and') {
            const { conditions } = visibilityConditions;
            for (const condition of conditions) {
                const edge = createConditionEdge(
                    condition,
                    currentId,
                    `condition-target-1`,
                    mappedDecisionIds
                );
                edges.push(edge);
            }
        } else {
            const { conditions } = visibilityConditions;
            for (const condition of conditions) {
                const edge = createConditionEdge(
                    condition,
                    currentId,
                    `condition-target-${conditionGroups}`,
                    mappedDecisionIds
                );
                edges.push(edge);
                conditionGroups += 1;
            }
        }
    } else {
        const edge = createConditionEdge(
            visibilityConditions,
            currentId,
            `condition-target-1`,
            mappedDecisionIds
        );
        edges.push(edge);
    }

    return { edges, conditionGroups };
}

function createConditionEdge(
    condition: Condition,
    currentId: string,
    targetHandle: string,
    mappedDecisionIds: Record<string, string>
) {
    const sourceId = mappedDecisionIds[condition.ref.id] ?? condition.ref.id;
    const updatedCondition = mappedDecisionIds[condition.ref.id]
        ? ({
              ref: {
                  id: mappedDecisionIds[condition.ref.id],
                  type: 'decision',
              },
              value: null,
              comparison: 'decision_is_completed',
          } as const)
        : condition;
    const sourceHandle = createConditionSourceHandle(updatedCondition);
    return createEdge(sourceId, sourceHandle, currentId, targetHandle, 'secondary');
}

function createFlowEdge(
    sourceId: string,
    sourceHandle: string,
    targetId: string,
    targetHandle: string
) {
    return createEdge(sourceId, sourceHandle, targetId, targetHandle, 'primary');
}

function createEdge(
    sourceId: string,
    sourceHandle: string,
    targetId: string,
    targetHandle: string,
    type: 'primary' | 'secondary'
) {
    return {
        id: `xy-edge__${sourceId}${sourceHandle}-${targetId}${targetHandle}`,
        source: sourceId,
        sourceHandle: sourceHandle,
        target: targetId,
        targetHandle: targetHandle,
        type: type,
    };
}

function calculateQuestionId(id: string, sectionId: string) {
    if (id.includes('.')) {
        const [firstNum, secondNum] = id.split('.');
        return `${sectionId}.${String(Number(firstNum) + Number(secondNum))}`;
    } else {
        return `${sectionId}.${id}`;
    }
}

function parseQuestion(
    question: Question,
    fullId: string,
    lastNodeId: string,
    lastNodeType: string,
    mappedDecisionIds: Record<string, string>,
    context: ContextVariable[]
): {
    node: { id: string; type: 'question'; data: QuestionNodeData };
    edges: FlowEdge[];
} {
    const edges = [];
    let conditionSlots = 1;
    const edge = createFlowEdge(
        lastNodeId,
        `${lastNodeType}-flow-source`,
        fullId,
        'question-flow-target'
    );
    edges.push(edge);
    if (question.visibility_conditions) {
        const { edges: conditionEdges, conditionGroups } = createConditionEdges(
            question.visibility_conditions,
            fullId,
            mappedDecisionIds
        );
        edges.push(...conditionEdges);
        conditionSlots = conditionGroups;
    }
    const node = {
        id: fullId,
        type: 'question' as const,
        data: {
            text: rewriteContextVariables(question.question_text.trim(), context),
            options: question.options?.map((opt) => opt.trim()) ?? [],
            skippable: question.skippable ?? false,
            spelling_sensitive: question.spelling_sensitive ?? false,
            required: question.required ?? false,
            visConditionSlots: conditionSlots,
        },
    };
    return { node, edges };
}

function parseSection(
    section: Action | Section,
    lastNodeId: string | null,
    lastNodeType: string | null,
    mappedDecisionNodes: Record<string, string>,
    context: ContextVariable[]
): {
    node:
        | { id: string; type: 'action'; data: ActionNodeData }
        | { id: string; type: 'section'; data: SectionNodeData };
    edges: FlowEdge[];
} {
    const edges = [];
    let conditionSlots = 1;
    const nodeType: 'section' | 'action' = section.questions ? 'section' : 'action';
    if (lastNodeId && lastNodeType) {
        const edge = createFlowEdge(
            lastNodeId,
            `${lastNodeType}-flow-source`,
            section.section_id,
            `${nodeType}-flow-target`
        );
        edges.push(edge);
    }
    const { section_id, section_name, section_description, display_number, visibility_conditions } =
        section;
    if (visibility_conditions) {
        const { edges: conditionEdges, conditionGroups } = createConditionEdges(
            visibility_conditions,
            section_id,
            mappedDecisionNodes
        );
        edges.push(...conditionEdges);
        conditionSlots = conditionGroups;
    }
    const isSubsection = section_id !== display_number;
    const isFirstSubsection = section_id.replace(display_number, '') === 'a';
    const node = {
        id: section.section_id,
        type: nodeType,
        data: {
            name: rewriteContextVariables(section_name.trim(), context),
            description: rewriteContextVariables(section_description.trim(), context),
            visConditionSlots: conditionSlots,
            isSubsection: isSubsection,
            isFirstSubsection: isFirstSubsection,
        },
    };
    return { node, edges };
}

function parseDecision(
    section: Decision,
    nextSection: Decision,
    lastNodeId: string | null,
    lastNodeType: string | null,
    context: ContextVariable[]
): { node: { id: string; type: 'decision'; data: DecisionNodeData }; edges: FlowEdge[] } {
    const edges = [];
    if (lastNodeId && lastNodeType) {
        const edge = createFlowEdge(
            lastNodeId,
            `${lastNodeType}-flow-source`,
            section.display_number,
            'decision-flow-target'
        );
        edges.push(edge);
    }
    const name = section.section_name.trim().split(' - ')[0];
    const firstSectionIsPassAction = section.visibility_conditions.value === true;

    const node = {
        id: section.display_number,
        type: 'decision' as const,
        data: {
            name: name,
            decisionVariable: section.visibility_conditions.ref.id,
            passed: firstSectionIsPassAction
                ? rewriteContextVariables(section.section_description.trim(), context)
                : rewriteContextVariables(nextSection.section_description.trim(), context),
            failed: firstSectionIsPassAction
                ? rewriteContextVariables(nextSection.section_description.trim(), context)
                : rewriteContextVariables(section.section_description.trim(), context),
            visConditionSlots: 0, // Lock at 0
        },
    };
    return { node, edges };
}

function parseFlow(sections: SchemaSection[], context: ContextVariable[]) {
    const nodes = [];
    const edges = [];
    let xOffset = 0;
    let yOffset = 0;
    let lastNodeId = null;
    let lastNodeType = null;
    let mappedDecisionIds: Record<string, string> = {};

    for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const { section_id, display_number, questions } = section;

        xOffset = (Number(display_number) - 1) * 40;

        if (isDecision(section)) {
            const nextSection = sections[i + 1] as Decision;
            const { node, edges: decisionEdges } = parseDecision(
                section,
                nextSection,
                lastNodeId,
                lastNodeType,
                context
            );
            mappedDecisionIds = mapDecisionNodeIds(
                mappedDecisionIds,
                [section_id, nextSection.section_id],
                display_number
            );
            nodes.push({ ...node, position: { x: xOffset, y: yOffset } });
            edges.push(...decisionEdges);
            lastNodeId = display_number;
            lastNodeType = 'decision';
            xOffset += 550;
            yOffset += 300;
            i += 1;
            continue;
        }

        const { node, edges: sectionEdges } = parseSection(
            section,
            lastNodeId,
            lastNodeType,
            mappedDecisionIds,
            context
        );
        nodes.push({ ...node, position: { x: xOffset, y: yOffset } });
        edges.push(...sectionEdges);
        lastNodeId = section_id;
        lastNodeType = questions ? 'section' : 'action';
        xOffset += 550;
        yOffset += 120;

        if (questions) {
            for (const question of questions) {
                const questionId = calculateQuestionId(question.question_id, section_id);
                const { node, edges: questionEdges } = parseQuestion(
                    question,
                    questionId,
                    lastNodeId,
                    lastNodeType,
                    mappedDecisionIds,
                    context
                );
                nodes.push({ ...node, position: { x: xOffset, y: yOffset } });
                edges.push(...questionEdges);
                lastNodeId = questionId;
                lastNodeType = 'question';
                xOffset += 650;
                yOffset += 120;
            }
        }
        yOffset += 180;
    }
    return { nodes, edges, context };
}

function parseContext(context: ContextItem[]): ContextVariable[] {
    return context.map((c) => ({
        name: c.name,
        type: 'string' as const,
        required: c.required,
        description: c.description,
        defaultValue: '',
    }));
}

export function parseSchema(schema: AIFlowSchema) {
    const context = parseContext(schema.context_schema);
    return parseFlow(schema.sections, context);
}

export function importJSON(
    file: File,
    setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>,
    setEdges: React.Dispatch<React.SetStateAction<FlowEdge[]>>,
    setContextVariables: React.Dispatch<React.SetStateAction<ContextVariable[]>>
): Promise<void> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                if (typeof e.target?.result === 'string') {
                    const json = JSON.parse(e.target.result);
                    const { nodes, edges, context } = parseSchema(json);
                    setNodes(nodes);
                    setEdges(edges);
                    setContextVariables(context);
                    resolve();
                } else {
                    throw new Error('File read error');
                }
            } catch (error) {
                console.error('Error parsing file:', error);
                reject(error);
            }
        };
        reader.readAsText(file);
    });
}
